import React, { lazy, Suspense, useEffect } from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import { useSelector } from "react-redux";

import { ErrorBoundary } from "./ErrorBoundary";
import { PUBLIC_ROUTE } from "./route.constants";
import Loader from "./Services/Restore/components/utility/loader";
import sessionManager from "./Services/Restore/library/helpers/sessionManager";
import DashboardRouter from "./Services/common/components/DashboardRouter";
import PhoneEntry from "./Services/Sleep/containers/TechLists/phoneEntry";
import NoAccess from "./Services/common/components/NoAccess";
import AccessPending from "./Services/common/components/AccessPending";
import RestoreDashboardGuard from "./Services/common/components/RestoreDashboardGuard";
import cookieHelper from "./Services/Restore/library/helpers/cookieHelper";

// More explicit lazy imports with error handling
const Dashboard = lazy(() => 
  import("./Services/Restore/containers/Dashboard/Dashboard")
    .then((module) => ({
      default: (props) => (
        <RestoreDashboardGuard>
          <module.default {...props} />
        </RestoreDashboardGuard>
      ),
    }))
    .catch(() => ({
      default: () => <div>Error loading Dashboard</div>,
    }))
);
const SleepDashboard = lazy(() => 
  import("./Services/Sleep/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading Sleep Dashboard</div>
  }))
);
const SleepTechLists = lazy(() =>
  import("./Services/Sleep/containers/TechLists").catch(() => ({
    default: () => <div>Error loading technician lists</div>
  }))
);
const DMEDashboard = lazy(() => 
  import("./Services/DME/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading DME Dashboard</div>
  }))
);
const ITDashboard = lazy(() => 
  import("./Services/IT/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading IT Dashboard</div>
  }))
);
const MarketingDashboard = lazy(() => 
  import("./Services/Marketing/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading Marketing Dashboard</div>
  }))
);
const AdminConsoleDashboard = lazy(() => 
  import("./Services/common/containers/AdminConsole/AdminConsoleDashboard").catch(() => ({
    default: () => <div>Error loading Admin Console</div>
  }))
);
const StaffingDashboard = lazy(() =>
  import("./Services/Staffing/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading Staffing Dashboard</div>
  }))
);
const DocManagementDashboard = lazy(() => 
  import("./Services/DocumentManagement/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading Document Management Dashboard</div>
  }))
);
const DmpDashboard = lazy(() =>
  import("./Services/DMP/containers/Dashboard/Dashboard").catch(() => ({
    default: () => <div>Error loading DMP Dashboard</div>
  }))
);
const Home = lazy(() => 
  import("./Services/common/components/Home").catch(() => ({
    default: () => <div>Error loading Home</div>
  }))
);

const publicRoutes = [
  {
    path: PUBLIC_ROUTE.LANDING,
    exact: true,
    component: lazy(() => import("./Services/Restore/containers/Pages/SignIn/SignIn").catch(() => ({
      default: () => <div>Error loading Sign In page. Please refresh.</div>
    }))),
  },
  {
    path: PUBLIC_ROUTE.SIGN_IN,
    component: lazy(() => import("./Services/Restore/containers/Pages/SignIn/SignIn").catch(() => ({
      default: () => <div>Error loading Sign In page. Please refresh.</div>
    }))),
  },
  {
    path: PUBLIC_ROUTE.FORGET_PASSWORD,
    component: lazy(() =>
      import("./Services/Restore/containers/Pages/ForgotPassword/ForgotPassword").catch(() => ({
        default: () => <div>Error loading Forgot Password page. Please refresh.</div>
      }))
    ),
  },
  {
    path: PUBLIC_ROUTE.RESET_PASSWORD,
    component: lazy(() =>
      import("./Services/Restore/containers/Pages/ResetPassword/ResetPassword").catch(() => ({
        default: () => <div>Error loading Reset Password page. Please refresh.</div>
      }))
    ),
  },
  {
    path: "/dme/booking/:token",
    component: lazy(() => import("./Services/DME/containers/PublicBooking/PublicBooking").catch(() => ({
      default: () => <div>Error loading booking page. Please refresh.</div>
    }))),
  },
  {
    path: "/survey/:token",
    component: lazy(() => import("./Services/Marketing/containers/PublicSurvey/PublicSurvey").catch(() => ({
      default: () => <div>Error loading survey page. Please refresh.</div>
    }))),
  },
  {
    path: "/it/helpdesk/request",
    exact: true,
    component: lazy(() => import("./Services/IT/containers/Helpdesk/PublicTicketRequest").catch(() => ({
      default: () => <div>Error loading IT Helpdesk request page. Please refresh.</div>
    }))),
  },
  {
    path: "/it/helpdesk/status/:token",
    exact: true,
    component: lazy(() => import("./Services/IT/containers/Helpdesk/PublicTicketStatus").catch(() => ({
      default: () => <div>Error loading ticket status. Please refresh.</div>
    }))),
  },
];

function hasPendingAccess() {
  try {
    const details = cookieHelper.getUserDetails();
    if (details?.accessStatus === "pending") return true;
    return !!localStorage.getItem("pendingAccessRequest");
  } catch {
    return false;
  }
}

function PrivateRoute({ component: Component, ...rest }) {
  const isLoggedIn = useSelector((state) => state.Auth.idToken);
  
  // Initialize session manager when authenticated
  useEffect(() => {
    if (isLoggedIn) {
      sessionManager.init();
    }
    
    // Clean up on unmount
    return () => {
      if (isLoggedIn) {
        sessionManager.cleanup();
      }
    };
  }, [isLoggedIn]);
  
  return (
    <Route
      {...rest}
      render={props => {
        if (!isLoggedIn) {
          return (
            <Redirect
              to={{
                pathname: "/signin",
                state: { from: props.location }
              }}
            />
          );
        }

        // Pending SSO users must not reach any module dashboard.
        if (hasPendingAccess() && props.location.pathname !== "/access-pending") {
          return <Redirect to="/access-pending" />;
        }

        return (
          <PhoneEntry location={props.location}>
            <DashboardRouter>
              <Component {...props} />
            </DashboardRouter>
          </PhoneEntry>
        );
      }}
    />
  );
}

export default function AppRouter() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Switch>
          {publicRoutes.map((route, index) => {
            const Component = route.component;
            return (
              <Route
                key={index}
                path={route.path}
                exact={route.exact}
                render={props => (
                  <Suspense fallback={<Loader />}>
                    <Component {...props} />
                  </Suspense>
                )}
              />
            );
          })}
          
          {/* No Access Route */}
          <Route path="/no-access" component={NoAccess} />
          <Route path="/access-pending" component={AccessPending} />
          
          {/* Protected Dashboard Routes */}
          <PrivateRoute
            path="/home"
            component={Home}
          />
          <PrivateRoute
            path="/dashboard"
            component={Dashboard}
          />
          <PrivateRoute
            exact
            path="/sleep/tech"
            component={SleepTechLists}
          />
          <PrivateRoute
            path="/sleep/dashboard"
            component={SleepDashboard}
          />
          <PrivateRoute
            path="/dme/dashboard"
            component={DMEDashboard}
          />
          <PrivateRoute
            path="/dmp/dashboard"
            component={DmpDashboard}
          />
          <PrivateRoute
            path="/it/dashboard"
            component={ITDashboard}
          />
          <PrivateRoute
            path="/marketing/dashboard"
            component={MarketingDashboard}
          />
          <PrivateRoute
            path="/doc-management/dashboard"
            component={DocManagementDashboard}
          />
          <PrivateRoute
            path="/admin-console/dashboard"
            component={AdminConsoleDashboard}
          />
          <PrivateRoute
            path="/staffing/dashboard"
            component={StaffingDashboard}
          />
          
          {/* Error Pages */}
          <Route
            path={PUBLIC_ROUTE.PAGE_404}
            component={lazy(() => import("./Services/Restore/containers/Pages/404/404").catch(() => ({
              default: () => <div>404 - Page Not Found</div>
            })))}
          />
          <Route
            path={PUBLIC_ROUTE.PAGE_500}
            component={lazy(() => import("./Services/Restore/containers/Pages/500/500").catch(() => ({
              default: () => <div>500 - Server Error</div>
            })))}
          />
          
          {/* Catch-all route */}
          <Route
            path="*"
            render={() => <Redirect to={PUBLIC_ROUTE.PAGE_404} />}
          />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}
